import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();

    // The dialog is the scroll container on desktop, where the photos stack vertically. On a
    // phone they are laid out in a ROW and the scroller is the list itself, so scroll events
    // never reach the dialog - which is why the rail sat frozen on the first thumbnail no
    // matter how far you swiped. Both are listened to; only one fires at a time.
    for (const el of this.#scrollers) el.addEventListener('scroll', this.handleScroll, { passive: true });

    // Native dismissal (Esc via the browser, form submit, close()) does not pass through
    // closeDialog, so the page lock is released here as well as there.
    this.refs.dialog.addEventListener('close', this.#releaseScrollLock);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const el of this.#scrollers) el.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('close', this.#releaseScrollLock);
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#releaseScrollLock();
  }

  /** Every element that can be the scroll container for the zoomed photos. */
  get #scrollers() {
    const list = this.refs.dialog.querySelector('.dialog-zoomed-gallery');
    return list ? [this.refs.dialog, list] : [this.refs.dialog];
  }

  // ── Page scroll lock ────────────────────────────────────────────────────────────────────
  // `overscroll-behavior: contain` on the dialog only helps while the dialog HAS scroll room to
  // absorb the gesture. On a phone it does not: the list scrolls horizontally and its vertical
  // overflow is hidden, so a vertical drag beside the photo found nothing to move inside the
  // dialog and went straight to the page behind it.
  // An attribute on <html> rather than the theme's DialogComponent lock: that one pins body to
  // position: fixed, and this dialog opens inside a view transition that morphs the thumbnail
  // into the full photo - repositioning body mid-transition breaks the morph. `overflow: hidden`
  // moves no layout, so the transition is untouched.
  #applyScrollLock = () => document.documentElement.setAttribute('zoom-scroll-lock', '');

  #releaseScrollLock = () => document.documentElement.removeAttribute('zoom-scroll-lock');

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      this.#applyScrollLock();
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // Carry over what the Liquid-rendered <img> had and this clone would otherwise drop: the inline
    // style holds --focal-point, and width/height give the browser an intrinsic ratio before the
    // bitmap is consulted. Not the cause of the flashing, but the swap should be a like-for-like
    // replacement rather than a differently-configured element.
    const inlineStyle = image.getAttribute('style');
    if (inlineStyle) newImage.setAttribute('style', inlineStyle);
    const w = image.getAttribute('width');
    const h = image.getAttribute('height');
    if (w) newImage.setAttribute('width', w);
    if (h) newImage.setAttribute('height', h);

    const swap = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;

    // Swap only once the bitmap is ready to PAINT, not merely loaded. `load` fires before the
    // image has been decoded, so replacing the node there lands on a frame where the incoming
    // <img> has nothing to show yet - which is the flash you see on every photo while scrolling
    // the zoomed gallery. decode() resolves after decoding, so the swap is invisible.
    // Falls back to load where decode() is unavailable, and on decode failure (e.g. the image
    // was evicted) so a broken decode can never leave the low-res image stuck in place.
    if (typeof newImage.decode === 'function') {
      newImage.decode().then(swap, swap);
    } else {
      newImage.onload = swap;
    }
  }

  #frame = 0;

  /** Index the rail is currently marking, so an unchanged frame costs nothing. */
  #markedIndex = -1;

  // ── Programmatic selection in flight ────────────────────────────────────────────────────
  // Clicking a thumbnail marks it and then smooth-scrolls the photos to it. That scroll fires
  // the scroll handler on every frame of the journey, and the handler dutifully marked whatever
  // photo was passing - so clicking the ninth thumbnail produced 8 → 0 → 1 → 2 … → 8: the mark
  // flew to the clicked thumbnail, got dragged back to the start and then crawled up the rail
  // while the rail chased it and reversed direction. That is the shivering.
  //
  // While a selection is travelling, the destination is the truth and the photos in between are
  // not; scroll-driven marking is therefore suspended until the destination arrives. If the
  // visitor grabs the scroll mid-flight the journey never completes, so #settle - which only
  // runs once scrolling has actually stopped - abandons the destination and marks reality.
  #pendingIndex = -1;

  #pendingSince = 0;

  /** How long a travelling selection may hold the mark before reality takes over. */
  static #PENDING_TIMEOUT = 1500;

  /**
   * Handles scrolling of the zoomed photos. Split in two on purpose:
   *
   *   - marking the thumbnail happens in the SAME frame as the scroll, from geometry
   *   - the expensive tail (fetching the high-resolution photo, telling the page's own gallery
   *     which slide to sit on) stays debounced
   *
   * It used to be one debounced call that also awaited an IntersectionObserver, so the mark
   * landed 50 ms after the swipe stopped plus an observer callback - on a phone, where each
   * swipe snaps to the next photo, that reads as the rail lagging a beat behind your thumb.
   * Nothing about the tail needs to be prompt, and nothing about the mark can afford not to be.
   */
  handleScroll = () => {
    if (!this.#frame) {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        const { index, ratio, held } = this.#measure();

        if (this.#pendingIndex >= 0) {
          const expired = performance.now() - this.#pendingSince > ZoomDialog.#PENDING_TIMEOUT;
          if (index === this.#pendingIndex || expired) {
            this.#pendingIndex = -1;
          } else {
            return;
          }
        }

        this.#markActive(index, ratio, held);
      });
    }
    this.#settle();
  };

  /**
   * The index of the photo currently filling most of the screen, measured synchronously.
   * Same notion of "most visible" as the IntersectionObserver it replaces - the largest
   * intersection RATIO, not area, so a tall photo is not favoured over a square one - but
   * available now rather than in a later frame.
   *
   * @returns {number} The index, or -1 if nothing is on screen.
   */
  #activeIndex() {
    return this.#measure().index;
  }

  /**
   * Measures which photo fills most of the screen.
   *
   * Also reports how much of the screen the CURRENTLY MARKED photo covers, because that - not
   * the runner-up - is what the challenger has to beat. Comparing winner against runner-up was
   * the first attempt and it had a hole: at a dead-even tie between two photos the gap is zero,
   * so the mark held - even when the photo it was holding on was nowhere on screen. Comparing
   * against the incumbent cannot do that: a mark on an off-screen photo has a ratio of 0, so
   * any real candidate beats it by a mile and the mark catches up immediately.
   *
   * @returns {{index: number, ratio: number, held: number}} Winner, its share of the screen,
   *   and the share currently held by the marked photo (0 if it is not on screen at all).
   */
  #measure() {
    const { media } = this.refs;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let index = -1;
    let ratio = 0;
    let held = 0;

    for (let i = 0; i < media.length; i++) {
      const r = media[i].getBoundingClientRect();
      const area = r.width * r.height;
      if (!area) continue;

      const w = Math.min(r.right, vw) - Math.max(r.left, 0);
      const h = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (w <= 0 || h <= 0) continue;

      const candidate = (w * h) / area;
      if (i === this.#markedIndex) held = candidate;
      if (candidate > ratio) {
        ratio = candidate;
        index = i;
      }
    }

    return { index, ratio, held };
  }

  /**
   * Marks a thumbnail as the active one and keeps it inside the rail.
   * @param {number} index - The index to mark.
   */
  #markActive(index, ratio = 1, held = 0, margin = 0.12) {
    if (index < 0) return;
    if (index === this.#markedIndex) return;

    // ── Hysteresis ─────────────────────────────────────────────────────────────────────────
    // Halfway between two photos both cover about half the screen, so the winner can change on
    // a single pixel of scroll - and momentum, scroll-snap settling and a trackpad's tiny
    // reversals all deliver exactly that. The mark then flickers between two neighbours, which
    // is the shivering. A challenger has to be ahead of the photo currently marked by a clear
    // margin before the mark moves; at the true crossover it is not, so the mark stays until the
    // new photo is properly in front. The margin is a share of the screen, not pixels, so it
    // holds at any viewport size.
    if (this.#markedIndex >= 0 && ratio - held < margin) return;

    const { thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    if (!(targetThumbnail instanceof HTMLElement)) return;

    this.#markedIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    this.#revealThumbnail(targetThumbnail);
  }

  /**
   * Brings a thumbnail into the rail's view, moving the rail as LITTLE as possible - and not at
   * all when the thumbnail is already visible.
   *
   * It used to centre the thumbnail on every change, which meant the rail glided on every photo
   * even though the marked thumbnail was already on screen. A rail in constant motion under a
   * mark that is also moving is the other half of the shivering. Centring is still right when
   * the visitor clicks a thumbnail (see selectThumbnail) - there the movement is the answer to
   * a deliberate action, not a side effect of scrolling.
   *
   * @param {HTMLElement} target - The thumbnail to reveal.
   */
  #revealThumbnail(target) {
    const rail = this.refs.thumbnails;
    if (!(rail instanceof HTMLElement)) return;

    const railRect = rail.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    // Aim a little past the edge, so the thumbnail lands clear of it rather than flush against
    // it - flush would mean the very next photo needs another scroll.
    const margin = 12;

    let top = rail.scrollTop;
    let left = rail.scrollLeft;
    let move = false;

    if (rail.scrollHeight > rail.clientHeight) {
      if (rect.top < railRect.top + margin) {
        top += rect.top - railRect.top - margin;
        move = true;
      } else if (rect.bottom > railRect.bottom - margin) {
        top += rect.bottom - railRect.bottom + margin;
        move = true;
      }
    }

    if (rail.scrollWidth > rail.clientWidth) {
      if (rect.left < railRect.left + margin) {
        left += rect.left - railRect.left - margin;
        move = true;
      } else if (rect.right > railRect.right - margin) {
        left += rect.right - railRect.right + margin;
        move = true;
      }
    }

    if (!move) return;

    rail.scrollTo({ top, left, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /** The part that can wait until the scrolling stops. */
  #settle = debounce(() => {
    // Scrolling has paused, so the mark can catch up with a much smaller margin than the moving
    // frames need. Not ZERO margin, though: at a genuine 50/50 tie - a finger resting on a
    // trackpad rocking a few pixels either side of the boundary - an unconditional mark flips
    // back and forth every time the wobble pauses, which is the shivering all over again, just
    // at 20Hz instead of 60. A tie leaves the mark where it is; anything with a real winner
    // moves it.
    this.#pendingIndex = -1;

    const { index, ratio, held } = this.#measure();
    if (index < 0) return;

    this.#markActive(index, ratio, held, 0.02);
    this.#loadHighResolutionImage(this.refs.media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Which photo is on screen, measured rather than observed - the same geometry the rail is
    // marked from, so the closing transition always morphs the photo the rail says you are on.
    const activeIndex = this.#activeIndex();
    const mostVisibleElement = media[activeIndex];
    if (!mostVisibleElement) return this.closeDialog();

    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#releaseScrollLock();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // Listování klávesnicí. Vodorovné i svislé šipky dělají totéž schválně: na desktopu jsou
    // fotky pod sebou a na telefonu vedle sebe, takže „další" má pokaždé jiný směr a nutit
    // uživatele hádat ten správný je zbytečné.
    const STEP = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const isJump = event.key === 'Home' || event.key === 'End';
    if (!(event.key in STEP) && !isJump) return;

    const count = this.refs.media.length;
    if (!count) return;

    // Výchozí bod je označená miniatura; když ještě žádná není (hned po otevření), dopočítá se
    // z geometrie, ať první stisk neskočí vždycky na první fotku.
    const from = this.#markedIndex >= 0 ? this.#markedIndex : this.#activeIndex();
    let target;
    if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = count - 1;
    else target = from + STEP[event.key];

    // Na krajích se nezacykluje - konec galerie má být poznat.
    if (target < 0 || target >= count) return;

    event.preventDefault();
    this.selectThumbnail(target, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    // Claim the destination before the scroll starts, so the frames of the journey cannot
    // repaint the mark on the photos being passed.
    this.#pendingIndex = index;
    this.#pendingSince = performance.now();
    this.#markedIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

